import { useState } from 'react'
import { X } from 'lucide-react'
import type { NtSegment, NtDay, NtActivityKind } from '../../core/types'
import { ACTIVITIES, activity, DAY_LABELS, SESSION_LABELS, segmentsIn, planSession, partsFor, planInBlock, PREP_DAY } from './model'
import { useNorseTerm } from './NorseTermData'

/**
 * The day planner — Tuesday to Thursday in the course's own block, in the two
 * parts the bell schedule gives it (90 minutes each on full days, 65 on
 * Wednesday's early out), then Friday morning's artifact prep.
 *
 * The prototype made this a drag-and-drop kanban, which is lovely on a laptop
 * and unusable on a phone. Here it is a grid you tap: pick the hour, pick what
 * kind of thing it is, type what happens. Same output, one interaction, works
 * with a thumb.
 *
 * Why the block *kinds* matter at all: a reviewer scanning thirty course plans
 * can see the shape of a week at a glance — whether it changes across four
 * days, whether anything is ever shown to anybody. That is the actual standard
 * the exemplars describe, and colour makes it readable in two seconds.
 */
export function DayPlanner({ plan, session, basis = 'assigned', readOnly, onChange }: {
  plan: NtSegment[]
  /** The session (0 = morning, 1 = afternoon) whose times to show. */
  session: 0 | 1
  /**
   * Why that session: the block the course was put in, the teacher's stated
   * preference, or no preference yet ("either"), in which case both sets of
   * times are shown.
   */
  basis?: 'assigned' | 'preferred' | 'either' | 'example'
  readOnly?: boolean
  onChange: (next: NtSegment[]) => void
}) {
  const [adding, setAdding] = useState<{ day: NtDay; part: number } | null>(null)
  const { term } = useNorseTerm()
  const block = session === 1 ? 'PM' : 'AM'
  const other = session === 1 ? 'AM' : 'PM'
  const current = planInBlock(plan, session)

  const add = (day: NtDay, part: number, span: number, kind: NtActivityKind, text: string) => {
    onChange([...current, { day, session: planSession(day, session), slot: part, span, kind, text }])
    setAdding(null)
  }
  const remove = (s: NtSegment) =>
    onChange(current.filter((p) => !(p.day === s.day && p.session === s.session && p.slot === s.slot)))

  return (
    <section style={{ marginTop: 'var(--s-5)' }}>
      <h3 style={{ fontSize: 'var(--t-md)', margin: '0 0 var(--s-1)' }}>The week</h3>
      <p style={{ color: 'var(--ink-2)', fontSize: 'var(--t-sm)', margin: '0 0 var(--s-3)' }}>
        {basis === 'example'
          ? <>This course meets in the <strong>{SESSION_LABELS[session].toLowerCase()}</strong>, Tuesday to Thursday, in two parts with a break between.</>
          : basis === 'assigned'
          ? <>Your course meets in the <strong>{SESSION_LABELS[session].toLowerCase()}</strong>, Tuesday to Thursday, in two parts with a break between.</>
          : basis === 'preferred'
            ? <>Times are shown for the <strong>{SESSION_LABELS[session].toLowerCase()}</strong>, the block you asked for. Admin decides the final block; if it changes, your plan moves with it.</>
            : <>You haven&apos;t picked a block, so each part shows both the morning and afternoon times. Plan Part 1 and Part 2 of each day; your plan fits whichever block admin puts you in.</>}
        {' '}Wednesday is an early out, so its parts are shorter. Friday morning is artifact prep with the
        students making their artifact in your course, before the open house.
      </p>

      {(DAY_LABELS as readonly string[]).map((label, dIdx) => {
        const day = dIdx as NtDay
        const s = planSession(day, session)
        const parts = partsFor(term, day, block)
        const rows = segmentsIn(current, day, s)
        const prep = day === PREP_DAY
        return (
          <div key={label} className="plan__day">
            <div className="plan__dayname">
              {label}{prep ? ': artifact & open-house prep (optional to plan)' : ''}
            </div>
            <div className="plan__session">
              {parts.map((part, pIdx) => {
                const seg = rows.find((r) => r.slot <= pIdx && pIdx < r.slot + r.span)
                if (seg && seg.slot !== pIdx) return null   // drawn once, by the part it starts in
                const nextFree = pIdx + 1 < parts.length && !rows.some((r) => r.slot <= pIdx + 1 && pIdx + 1 < r.slot + r.span)
                return (
                  <div key={pIdx} className="plan__part">
                    <div className="plan__label">
                      {parts.length > 1 ? `Part ${pIdx + 1}` : 'Morning'} · {part.start}–{seg && seg.span > 1 ? parts[pIdx + 1]?.end : part.end}
                      {basis === 'either' && !prep && (() => {
                        const alt = partsFor(term, day, other)
                        const a = alt[pIdx]
                        const end = seg && seg.span > 1 ? alt[pIdx + 1]?.end : a?.end
                        return a ? <> {SESSION_LABELS[session].toLowerCase()} <strong>OR</strong> {a.start}–{end} {SESSION_LABELS[session === 1 ? 0 : 1].toLowerCase()}</> : null
                      })()}
                      {seg && seg.span > 1 && ' (both parts)'}
                    </div>
                    {seg ? (
                      <div className="plan__seg" style={{ borderLeftColor: activity(seg.kind).colour }}>
                        <div className="plan__segmain">
                          <span className="plan__kind" style={{ color: activity(seg.kind).colour }}>{activity(seg.kind).label}</span>
                          <span className="plan__text">{seg.text}</span>
                        </div>
                        {!readOnly && (
                          <button className="opt__icon opt__icon--danger" aria-label={`Remove ${seg.text}`} onClick={() => remove(seg)}>
                            <X size={15} aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    ) : readOnly ? (
                      <div className="plan__empty">Nothing planned</div>
                    ) : adding?.day === day && adding.part === pIdx ? (
                      <SegForm canSpan={nextFree} onCancel={() => setAdding(null)}
                        onAdd={(span, kind, text) => add(day, pIdx, span, kind, text)} />
                    ) : (
                      <button className="plan__add" onClick={() => setAdding({ day, part: pIdx })}>+ Plan this part</button>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}

      <div className="plan__key">
        {ACTIVITIES.map((a) => (
          <span key={a.k} className="plan__keyitem">
            <span className="plan__dot" style={{ background: a.colour }} aria-hidden="true" />
            {a.label}
          </span>
        ))}
      </div>
    </section>
  )
}

function SegForm({ canSpan, onAdd, onCancel }: {
  /** The next part is free too, so this can fill the whole block. */
  canSpan: boolean
  onAdd: (span: number, kind: NtActivityKind, text: string) => void
  onCancel: () => void
}) {
  const [span, setSpan] = useState(1)
  const [kind, setKind] = useState<NtActivityKind>('hands')
  const [text, setText] = useState('')

  return (
    <div className="plan__form">
      <input className="opt__input" autoFocus placeholder="What happens in this part"
        aria-label="What happens" value={text} onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && text.trim()) { e.preventDefault(); onAdd(span, kind, text.trim()) } }} />
      <div className="plan__formrow">
        <select className="opt__input" aria-label="Kind" value={kind}
          onChange={(e) => setKind(e.target.value as NtActivityKind)}>
          {ACTIVITIES.map((a) => <option key={a.k} value={a.k}>{a.label}</option>)}
        </select>
        {canSpan && (
          <select className="opt__input" aria-label="How long" value={span}
            onChange={(e) => setSpan(Number(e.target.value))} style={{ maxWidth: 160 }}>
            <option value={1}>This part</option>
            <option value={2}>Both parts</option>
          </select>
        )}
      </div>
      <div className="plan__formrow">
        <button className="btn" disabled={!text.trim()} onClick={() => onAdd(span, kind, text.trim())}>Add</button>
        <button className="btn btn--ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}
